import React, { useCallback, useEffect, useState } from 'react';
import { ImageDown, Loader2, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { brandsAPI, uploadsAPI } from '../services/api';
import { toast } from 'sonner';

// Admin › Marques : fetch the missing logos (equivalence brands + catalogue) automatically, with progress + results
const BrandLogoFetchCard = ({ onChanged }) => {
  const [st, setSt] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => brandsAPI.logoFetchStatus().then(setSt).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const running = !!st?.job?.running;
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(async () => {
      try {
        const s = await brandsAPI.logoFetchStatus();
        setSt(s);
        if (!s.job?.running) { clearInterval(id); toast.success(`Logos récupérés : ${s.job?.found || 0}/${s.job?.total || 0}`); onChanged?.(); }
      } catch { /* retry */ }
    }, 2500);
    return () => clearInterval(id);
  }, [running, onChanged]);

  const start = async (force = false) => {
    try {
      const r = await brandsAPI.logoFetchStart(force && st?.failed?.length ? st.failed.map(f => f.name) : null, force);
      if (!r.started) toast.info(r.message);
      setSt(s => ({ ...s, job: r.job }));
      setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Lancement impossible'); }
  };
  const forget = async (name) => {
    try { await brandsAPI.logoForget(name); toast.success(`Logo retiré pour ${name}`); load(); onChanged?.(); } catch { toast.error('Suppression impossible'); }
  };

  if (!st) return null;
  const results = st.job?.results || [];
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="brand-logo-fetch-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><ImageDown size={16} className="text-[#1e3a5f]" /> Logos manquants (marques d'équivalence et catalogue)</h3>
          <p className="text-xs text-gray-500" data-testid="brand-logo-fetch-summary">
            {st.missing_count} marque(s) sans logo · {st.fetched} logo(s) récupéré(s) automatiquement · {st.failed?.length || 0} introuvable(s)
            {running && <span className="ml-2 text-orange-600 font-medium" data-testid="brand-logo-fetch-progress">· en cours {st.job.done}/{st.job.total} {st.job.current ? `(${st.job.current})` : ''}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => start(false)} disabled={running || !st.missing_count || !st.ai_ready} data-testid="brand-logo-fetch-start" className="bg-[#1e3a5f] hover:bg-[#162c48] text-white" title={st.ai_ready ? "L'IA retrouve le site officiel, puis logo Wikidata ou favicon HD (60 marques max par passage ; le reste est complété chaque matin)" : 'Clé IA absente'}>
            {running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ImageDown size={14} className="mr-1" />} Récupérer les logos manquants ({Math.min(st.missing_count, 60)})
          </Button>
          {!!st.failed?.length && <Button size="sm" variant="outline" onClick={() => start(true)} disabled={running} data-testid="brand-logo-fetch-retry">Réessayer les introuvables ({st.failed.length})</Button>}
          <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-0.5" data-testid="brand-logo-fetch-toggle">{open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} détails</button>
        </div>
      </div>
      {open && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">Dernier passage ({results.length})</p>
            {!results.length ? <p className="text-xs text-gray-400">Aucun passage pour le moment.</p> : (
              <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto border border-gray-100 rounded-md" data-testid="brand-logo-fetch-results">
                {results.map(r => (
                  <li key={r.name} className="flex items-center gap-2 px-2 py-1.5" data-testid={`brand-logo-result-${r.name}`}>
                    {r.logo ? <img src={uploadsAPI.resolveUrl(r.logo)} alt="" className="h-5 w-9 object-contain" /> : <span className="h-5 w-9 rounded bg-gray-100 text-[9px] text-gray-400 flex items-center justify-center">—</span>}
                    <span className="font-medium text-gray-800 flex-1 truncate">{r.name}</span>
                    <span className="text-[11px] text-gray-400 truncate max-w-[140px]">{r.domain || 'site inconnu'}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.source === 'wikidata' ? 'bg-emerald-50 text-emerald-700' : r.logo ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{r.source === 'wikidata' ? 'Wikidata' : r.source === 'favicon' ? 'favicon' : r.source === 'site' ? 'site officiel' : 'introuvable'}</span>
                    {r.logo && <button type="button" onClick={() => forget(r.name)} title="Retirer ce logo (mauvais visuel)" className="text-gray-400 hover:text-red-600" data-testid={`brand-logo-forget-${r.name}`}><Trash2 size={13} /></button>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">Encore sans logo ({st.missing_count})</p>
            <p className="text-xs text-gray-500 leading-relaxed max-h-72 overflow-y-auto" data-testid="brand-logo-fetch-missing">{st.missing.join(' · ') || 'Toutes les marques ont un logo.'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandLogoFetchCard;
